import {Component, effect, inject, OnInit, signal, untracked} from '@angular/core';
import {NgClass} from "@angular/common";
import {DataSubscriptionService} from "src/app/infrastructure/data-subscription.service";
import {GenericButtonComponent} from "src/app/presentation/common/generic-button/generic-button.component";
import {ToCapitalizedTextPipe} from "src/app/presentation/common/pipes/ToCapitalizedTextPipe";
import {DataSynchronisationService} from "src/app/infrastructure/data-synchronisation.service";
import {ViewportService} from "src/app/infrastructure/viewport.service";
import {ActionType, DataType} from "src/app/domain/domain-models";
import {
  allAnimalTypes,
  Animal,
  AnimalType,
  Device,
  DeviceAssociation,
  Paddock,
  SynchronizationStatus,
} from "src/app/infrastructure/moovement-api/models/asset";
import {FormsModule} from "@angular/forms";
import {
  CreateWeightMeasurementComponent
} from "src/app/presentation/main-view/information-center/animals/add-weight-measurement/create-weight-measurement.component";
import {AddWeightMeasurementRequest} from 'src/app/infrastructure/moovement-api/web-api/animal.api-client';
import {
  AnimalButtonComponent
} from "src/app/presentation/main-view/information-center/animals/animal-button/animal-button.component";
import {FarmStore} from 'src/app/domain/assets/farm/farm.store';
import {
  DeviceIdInputComponent,
  DeviceIdSelected
} from "src/app/presentation/common/device-id-input/device-id-input.component";
import {
  InternalNotificationsStore,
  InternalNotificationType
} from 'src/app/infrastructure/internal-notifications/internal-notifications.store';
import {ActivatedRoute, Router} from '@angular/router';
import {randomUuid} from 'src/app/domain/util/uuid/uuid';
import {DeviceStore} from 'src/app/domain/devices/device.store';
import {AnimalsStore} from 'src/app/domain/assets/animals/animals.store';
import {DeviceAssociationStore} from 'src/app/domain/devices/device-association.store';
import {CloseButtonComponent} from "src/app/presentation/main-view/close-button/close-button.component";
import {BackButtonComponent} from "src/app/presentation/main-view/back-button/back-button.component";
import {unassignedAnimalsPaddock} from 'src/app/domain/assets/paddocks/unassigned-animals-paddock';
import {AddAnimalStore} from 'src/app/presentation/main-view/information-center/animals/add-animal/add-animal.store';
import {
  ProfilePictureEditorComponent
} from "src/app/presentation/main-view/information-center/animals/profile-picture-editor/profile-picture-editor.component";
import {PaddockStore} from 'src/app/domain/assets/paddocks/paddock.store';
import {isBluetoothProximitySensor} from 'src/app/domain/devices/bluetooth-proximity-sensor/bluetooth-proximity-sensor';
import {ScheduledUpdates} from 'src/app/infrastructure/scheduled-updates/scheduled-updates';

export enum Destination {
  UNASSIGNED_TAGS = "UNASSIGNED_TAGS"
}

@Component({
  selector: 'app-add-animal',
  imports: [
    GenericButtonComponent,
    ToCapitalizedTextPipe,
    NgClass,
    FormsModule,
    CreateWeightMeasurementComponent,
    AnimalButtonComponent,
    DeviceIdInputComponent,
    CloseButtonComponent,
    BackButtonComponent,
    ProfilePictureEditorComponent
],
  templateUrl: './add-animal.component.html',
  styleUrl: './add-animal.component.css'
})
export class AddAnimalComponent implements OnInit {

  deviceIdentifier?: string
  cameraAccessGranted: boolean = true;
  deviceToMatch?: Device;
  existingDeviceAssociation = signal<DeviceAssociation | undefined>(undefined);
  existingAnimal = signal<Animal | undefined>(undefined);
  paddocks = signal<Paddock[]>([]);
  years: Array<number> = []
  animalTypes: Array<AnimalType> = allAnimalTypes
  animals: Array<Animal> = []

  loading = signal(true);

  addAnimalStore = inject(AddAnimalStore);

  private hardwareId: string | null = null;
  private destination?: string;

  private activatedRoute = inject(ActivatedRoute);
  private animalStore = inject(AnimalsStore);
  private deviceAssociationStore = inject(DeviceAssociationStore);
  private deviceStore = inject(DeviceStore);
  private farmStore = inject(FarmStore);
  private internalNotificationsStore = inject(InternalNotificationsStore);
  private paddockStore = inject(PaddockStore);
  private router = inject(Router);

  constructor(
    protected dataSynchronisationService: DataSynchronisationService,
    protected dataSubscriptionService: DataSubscriptionService,
    protected viewportService: ViewportService
  ) {
    this.activatedRoute.queryParamMap.subscribe(map => {
      const hardwareId = map.get('hardwareId');
      this.hardwareId = hardwareId;

      const destination = map.get('destination');
      this.destination = this.toDestination(destination);
    });

    effect(() => {
      const loadingDevices = this.deviceStore.loading();
      const loadingAnimals = this.animalStore.loading();
      if (loadingAnimals || loadingDevices) return;

      this.loading.set(false);

      const hardwareId = this.hardwareId;
      if (!hardwareId) return;

      untracked(() => this.processDeviceId(hardwareId, true));
    });

    effect(() => {
      if (this.paddockStore.loading()) return;

      const paddocks = this.paddockStore.entities();
      this.paddocks.set([...paddocks, unassignedAnimalsPaddock]);
    });
  }

  private toDestination(destination: string | null) {
    if (destination == Destination.UNASSIGNED_TAGS) return Destination.UNASSIGNED_TAGS;

    return undefined;
  }

  ngOnInit() {
    const today = new Date();
    for (let i = 0; i < 10; i++) {
      this.years.push(today.getFullYear() - i)
    }
  }

  onDeviceIdSelected(deviceIdSelected: DeviceIdSelected) {
    const id = deviceIdSelected.id;
    if (!id) return;

    this.processDeviceId(id, !deviceIdSelected.manualInput);
  }

  processDeviceId(id: string, hardwareId: boolean) {
    const devices = this.deviceStore.entities();
    let device: Device | undefined;
    if (hardwareId) {
      device = devices.find(device => device.hardwareId?.toLowerCase() === id.toLowerCase());
    } else {
      device = devices.find(device => device.id?.toLowerCase()?.endsWith(id.toLowerCase()));
    }
    if (!device) {
      this.internalNotificationsStore.addNotification(`Tag '${id}' is not assigned to your farm.`, InternalNotificationType.ERROR, false);
      return;
    }

    this.deviceIdentifier = device.id.slice(-5);

    if (isBluetoothProximitySensor(device)) {
      this.internalNotificationsStore.addNotification(
        `Device ${this.deviceIdentifier} is a BLE scanner. You can't assign BLE scanners to animals. Please contact your mOOvement contact person.`,
        InternalNotificationType.ERROR,
        false
      );
      return;
    }

    this.deviceToMatch = device;
    this.processMatchingDeviceId();
  }

  setYob(year: number) {
    this.addAnimalStore.setYear(year);
  }

  setAnimalType(animalType: AnimalType) {
    this.addAnimalStore.setAnimalType(animalType);
  }

  setPaddock(paddock: Paddock) {
    this.addAnimalStore.setPaddock(paddock);
  }

  canProcessNewAnimal(): boolean {
    return !this.loading() && this.addAnimalStore.name() !== undefined && this.farmStore.farm() !== undefined;
  }

  setName(event: Event) {
    this.addAnimalStore.setName(this.getStringValue(event));
  }

  setComment(event: Event) {
    this.addAnimalStore.setComment(this.getStringValue(event));
  }

  setProfilePictureUrl(imageUrl: string | undefined) {
    this.addAnimalStore.setProfilePictureUrl(imageUrl);
  }

  private getStringValue(event: Event) {
    if (!event.target) return;

    const string = (event.target as HTMLInputElement).value;

    return string === '' ? undefined : string;
  }

  setWeightMeasurement(measurement?: AddWeightMeasurementRequest) {
    this.addAnimalStore.setWeightMeasurement(measurement);
  }

  async processNewAnimal() {
    if (!this.canProcessNewAnimal()) return;

    this.loading.set(true);

    const newId = randomUuid();
    const newAnimal: Animal = {
      id: newId,
      version: 0,
      farmId: this.farmStore.farm()!.id,
      synchronizationStatus: SynchronizationStatus.syncPending,
      visualTag: this.addAnimalStore.name(),
      yearOfBirth: this.addAnimalStore.year()?.toString(),
      type: this.addAnimalStore.animalType(),
      assignedPaddockId: this.paddockId(),
      comment: this.addAnimalStore.comment()
    };

    try {
      await this.dataSynchronisationService.applyAction(
        DataType.animals,
        ActionType.mergeAsset,
        newAnimal,
        SynchronizationStatus.syncPending
      );

      await this.dataSynchronisationService.applyAction(
        DataType.deviceAssociations,
        ActionType.associateAssetWithDevice,
        {
          id: this.deviceToMatch?.id! + newId,
          version: 0,
          startDateTime: new Date().toISOString(),
          assetId: newId,
          deviceId: this.deviceToMatch?.id,
          assetState: SynchronizationStatus.syncPending
        },
        SynchronizationStatus.syncPending
      );

      const weightMeasurement = this.addAnimalStore.weightMeasurement();
      if (weightMeasurement) {
        const updates: ScheduledUpdates = {
          id: randomUuid(),
          updates: [
            {
              type: "addWeightMeasurement",
              data: {
                animalId: newId,
                weightMeasurement,
              }
            }
          ],
          failureMessage: `Unable to add weight measurement for '${this.addAnimalStore.name()}'.`
        }
        await this.dataSynchronisationService.scheduleUpdates(updates);
      }

      const profilePictureUrl = this.addAnimalStore.profilePictureUrl();
      if (profilePictureUrl) {
        const updates: ScheduledUpdates = {
          id: randomUuid(),
          updates: [
            {
              type: "setProfilePicture",
              data: {
                animalId: newId,
                profilePictureUrl,
              }
            }
          ],
          failureMessage: `Unable to set profile picture for '${this.addAnimalStore.name()}'.`
        }
        await this.dataSynchronisationService.scheduleUpdates(updates);
      }

      this.internalNotificationsStore.addNotification("Successfully added animal.", InternalNotificationType.SUCCESS, false);

      if (this.destination === Destination.UNASSIGNED_TAGS) {
        this.router.navigate(['/main/unassigned-tags']);
      }

      this.reset();
    } catch (error) {
      this.internalNotificationsStore.addNotification("Unable to add animal.", InternalNotificationType.ERROR, true);
      this.loading.set(false);
    }
  }

  private paddockId() {
    const paddock = this.addAnimalStore.paddock();
    if (!paddock) return;

    return paddock === unassignedAnimalsPaddock ? undefined : paddock.id;
  }

  private processMatchingDeviceId() {
    const deviceAssociations = this.deviceAssociationStore.entities();
    const existingDeviceAssociations = deviceAssociations.filter(deviceAssociation => deviceAssociation.deviceId == this.deviceToMatch?.id);
    const existingDeviceAssociation = existingDeviceAssociations.at(0);
    if (existingDeviceAssociation) {
      this.existingDeviceAssociation.set(existingDeviceAssociation);
      const animals = this.animalStore.entities();
      const existingAnimal = animals.find(animal => animal.id == existingDeviceAssociation.assetId);
      this.existingAnimal.set(existingAnimal);
    }
  }

  async deleteDeviceAssociation() {
    const association = this.existingDeviceAssociation();
    if (!association) return;

    const animal = this.existingAnimal();
    if (animal) {
      await this.dataSynchronisationService.dissociateAsset(animal, DataType.animals);
      const name = animal.visualTag ? animal.visualTag : animal.id;
      const updates: ScheduledUpdates = {
        id: randomUuid(),
        updates: [
          {
            type: "setProfilePicture",
            data: {
              animalId: animal.id,
              profilePictureUrl: undefined,
            }
          }
        ],
        failureMessage: `Unable to clear profile picture for '${name}'.`
      }
      await this.dataSynchronisationService.scheduleUpdates(updates);
      this.existingAnimal.set(undefined);
    } else {
      await this.dataSynchronisationService.dissociateDeviceAssociation(association);
    }

    this.existingDeviceAssociation.set(undefined);

    this.internalNotificationsStore.addNotification("Successfully deleted animal.", InternalNotificationType.SUCCESS, false);
  }

  reset() {
    this.loading.set(false);
    this.deviceIdentifier = undefined;
    this.deviceToMatch = undefined;
    this.existingDeviceAssociation.set(undefined);
    this.existingAnimal.set(undefined);
    this.addAnimalStore.reset();
  }
}
